import { roundToTwoDecimals } from "@/components/utils/commonUtils";
import { calculateDuration, getJstTime } from "@/components/utils/dateUtils";
import { i18n } from "@/locales/i18n";
import type { CampaignResult, CampaignWithId } from "@/resources/campaign";
import type { CampaignCouponWithId } from "@/resources/coupon";
import type {
  CanDoAchievement,
  CefrResults,
  DialogCanDoItem,
  DialogWithRelations,
} from "@/resources/dialog";
import { isCanDoAchievement } from "@/resources/dialog";
import type { CampaignResultsRow } from "@/resources/package";
import type { ScenarioWithId } from "@/resources/scenario";
import { determineCampaignStatus } from "./determineCampaignStatus";

/**
 * Transforms a single campaign result from the backend into the frontend format
 * This is shared logic used by both getProcessedCampaignResults and getProcessedPackageResults
 */

export function transformCampaignResult(
  campaignResult: CampaignResult,
  campaign: CampaignWithId,
  scenarioShouldAssess?: boolean,
): CampaignResultsRow[] | null {
  if (!campaignResult || !campaign) {
    return null;
  }

  const returnedRows: CampaignResultsRow[] = [];

  const baseRow: CampaignResultsRow = {
    campaignId: campaign.id,
    couponCode: campaignResult.coupon.couponCode,
    dialogId: "",
    displayName: "",
    status: "Unregistered",
    startedAt: "",
    completedAt: "",
    duration: "",
    overall: "",
    range: "",
    accuracy: "",
    phonology: "",
    fluency: "",
    coherence: "",
    interaction: "",
    coupon: campaignResult.coupon,
    ticket: campaignResult.ticket || undefined,
  };

  const resultCouponHasLearnerIdAndIsConfirmed =
    campaignResult.coupon.learnerId && campaignResult.coupon.isConfirmed;

  const orderedDialogs = (campaignResult.dialogs || [])
    .filter((d) => d.canceledAt === null || d.canceledAt === undefined)
    .slice()
    .sort((a, b) => {
      if (a.reservedAt !== b.reservedAt) {
        return a.reservedAt - b.reservedAt;
      }
      const aStart = a.startedAt ?? Number.POSITIVE_INFINITY;
      const bStart = b.startedAt ?? Number.POSITIVE_INFINITY;
      return aStart - bStart;
    });

  const resultHasDialogs = orderedDialogs.length > 0;

  if (!resultCouponHasLearnerIdAndIsConfirmed) {
    returnedRows.push(baseRow);
    return returnedRows;
  }

  if (resultCouponHasLearnerIdAndIsConfirmed && !resultHasDialogs) {
    returnedRows.push({
      ...baseRow,
      displayName: getDisplayName(campaignResult),
      status: "Registered",
    });
    return returnedRows;
  }

  for (const dialog of orderedDialogs) {
    const startedAt = dialog?.startedAt ? getJstTime(dialog.startedAt) : "";
    const completedAt = dialog?.endedAt ? getJstTime(dialog.endedAt) : "";

    const enhancedRow: CampaignResultsRow = {
      ...baseRow,
      dialogId: dialog.id,
      displayName: getDisplayName(campaignResult),
      status: determineCampaignStatus(
        campaignResult.coupon.isConfirmed,
        !!dialog.assessment,
        !!dialog.endedAt,
      ),
      startedAt,
      completedAt,
      duration:
        startedAt && completedAt
          ? calculateDuration(startedAt, completedAt)
          : "",
      overall: getScore("overall", dialog),
      range: getScore("range", dialog),
      accuracy: getScore("accuracy", dialog),
      phonology: getScore("phonology", dialog),
      fluency: getScore("fluency", dialog),
      coherence: getScore("coherence", dialog),
      interaction: getScore("interaction", dialog),
      shouldAssess: scenarioShouldAssess,
      score: dialog.assessment?.assessmentData?.cefrResults || undefined,
      ticket: dialog.ticket || undefined,
      canDo: getCanDoScoreRatio(dialog),
      canDoScores: getCanDoScores(dialog),
      review: getReviewPracticeCount(dialog),
    };

    returnedRows.push(enhancedRow);
  }

  return returnedRows;
}

const getDisplayName = (campaignResult: CampaignResult) => {
  if (!campaignResult.learner) {
    return i18n.t("campaignResult.deletedLearner") as string;
  }
  return campaignResult.learner.displayName || "";
};

const getScore = (
  type: string,
  dialog: DialogWithRelations,
  shouldAssess?: boolean,
) => {
  const cefrResults = dialog?.assessment?.assessmentData?.cefrResults || null;
  if (cefrResults && type in cefrResults) {
    const scoreData = cefrResults[type as keyof CefrResults];

    // Special case: interaction type with no label and score -1
    if (
      type === "interaction" &&
      scoreData &&
      !scoreData.label &&
      scoreData.score === -1
    ) {
      return "-1";
    }

    if (
      scoreData &&
      scoreData.label &&
      scoreData.score !== undefined &&
      (shouldAssess === undefined || shouldAssess)
    ) {
      return `${scoreData.label} (${roundToTwoDecimals(
        Number(scoreData.score),
      )})`;
    }
    return "";
  } else {
    return "";
  }
};

const CAN_DO_ACHIEVEMENT_POINTS: Record<CanDoAchievement, number> = {
  success: 1,
  partial: 0.5,
  fail: 0,
};

export const toAchievement = (item: DialogCanDoItem): CanDoAchievement => {
  if (isCanDoAchievement(item.achievement)) return item.achievement;
  return item.isAchieved ? "success" : "fail";
};

const formatCanDoScore = (score: number): string =>
  String(Math.round(score * 100) / 100);

export const getCanDoScores = (dialog: DialogWithRelations): number[] => {
  if (!dialog.gnowsisGroupTypes?.includes("can-do")) {
    return [];
  }

  if (!dialog.canDo || dialog.canDo.success !== true) {
    return [];
  }

  const canDos = dialog.canDo.result?.canDos;
  if (!canDos || canDos.length === 0) {
    return [];
  }

  return canDos
    .slice()
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
    .map((canDo) => CAN_DO_ACHIEVEMENT_POINTS[toAchievement(canDo)]);
};

export const getCanDoScoreRatio = (
  dialog: DialogWithRelations,
): string | undefined => {
  const scores = getCanDoScores(dialog);
  if (scores.length === 0) {
    return undefined;
  }

  const score = scores.reduce((total, points) => total + points, 0);

  return `${formatCanDoScore(score)}/${scores.length}`;
};

export const getReviewPracticeCount = (
  dialog: DialogWithRelations,
): string | undefined => {
  if (!dialog.gnowsisGroupTypes?.includes("review-feedback")) {
    return undefined;
  }

  if (!dialog.review || dialog.review.isFinished !== true) {
    return undefined;
  }

  const reviewCards = dialog.review?.reviewCards;
  if (!reviewCards || reviewCards.length === 0) {
    return undefined;
  }

  const practicedCards = reviewCards.filter(
    (card) => card.practicedAt !== null,
  );
  return `${practicedCards.length}/${reviewCards.length}`;
};

/**
 * Extracts coupon data from a campaign result
 */
export function extractCouponFromResult(
  result: CampaignResult,
): CampaignCouponWithId | null {
  return result?.coupon || null;
}

/**
 * Checks if scenario should assess based on campaign and scenario list
 */
export function checkScenarioShouldAssess(
  campaign: CampaignWithId,
  scenarioList: ScenarioWithId[],
): boolean {
  if (!campaign?.scenarioDocId) {
    return false;
  }

  const scenario = scenarioList.find(
    (s) =>
      s.id === campaign.scenarioDocId ||
      s.scenarioIdLegacy === campaign.scenarioDocId,
  );
  if (!scenario) {
    return false;
  }
  return scenario.shouldAssess || false;
}
